import { canonical, DAY_SETS, FAITHS, FOOD_RULES, isTradition, KITCHENS, TRADITIONS, } from './vocab.js';
// ============================================================================================
// GEREVIEWD BESTAND. Dit is de enige plek waar een traditie ("Turks", "Surinaams") gekoppeld
// wordt aan iets anders. Wijzigingen hier alleen na review door de oprichter (inhoud) en een
// privacy-check (docs/PRIVACY-gezinsprofiel.md): een verkeerde koppeling voelt als een aanname
// over iemands geloof of afkomst.
//
// Regels (docs/ONTWERP.md beslissingen 106 en 107):
// 1. Een traditie STELT VOOR, neemt nooit aan. Het voorstel ("Past dit bij jullie? Islam ·
//    Halal") is nooit voorgevinkt, kost één tik om over te nemen en blijft altijd te wijzigen.
// 2. Alleen voorstellen waar het een heel gebruikelijke keuze is. Turks en Marokkaans: Islam en
//    Halal. Geen geloofsvoorstel bij de andere tradities: die gezinnen zijn te divers.
// 3. De tekst zegt nooit dat een traditie iets bepaalt: "Past dit bij jullie?", nooit "Omdat jullie
//    Turks zijn".
// 4. Bijzondere dagen worden alleen AANGEBODEN (days.ts offeredDaySets); de ouder zet ze zelf aan.
//    De voorstelkaart mag ze noemen, "Ja, overnemen" zet ze nooit aan.
// 5. ALLEEN IN DE BROWSER. De server slaat precies op wat de ouder koos (faiths, food_rules,
//    kitchens, day_sets) en leidt nooit iets af uit tradities: apps/api en packages/db importeren
//    suggestFromTraditions, pendingSuggestion en acceptSuggestion niet (architectuurtest in
//    apps/api/src/profileContract.test.ts). Tradities worden niet bewaard (vocab.ts TRADITIONS).
// ============================================================================================
/** Welke keuken een traditie-chip bewaart (alleen volgorde, nooit verbergen). */
export const TRADITION_KITCHENS = {
    dutch: ['dutch'],
    turkish: ['turkish'],
    moroccan: ['moroccan'],
    surinamese: ['surinamese'],
    antillean: ['antillean'],
    indonesian: ['indonesian'],
    moluccan: ['moluccan'],
    italian: ['italian'],
    polish: ['polish'],
    middle_eastern: ['middle_eastern'],
    // "Anders" opent de keukenkeuze; zelf bewaart hij niets.
    other: [],
};
/** Keukens die we opslaan voor deze traditie-chips (canonieke volgorde, zonder dubbelen). */
export function traditionKitchens(traditions) {
    const out = [];
    for (const t of canonical(traditions, TRADITIONS))
        out.push(...TRADITION_KITCHENS[t]);
    return canonical(out, KITCHENS);
}
/**
 * Welke traditie-chips aangevinkt staan bij het openen van Instellingen: de keukens die een
 * traditiecode zijn. Zo toont Instellingen dezelfde herkenbare chips zonder dat we tradities
 * bewaren; "Anders" staat aan als er een keuken is die geen traditie is (Chinees, Mexicaans, ...).
 */
export function traditionsFromKitchens(kitchens) {
    const out = [];
    let extra = false;
    for (const k of canonical(kitchens, KITCHENS)) {
        if (isTradition(k))
            out.push(k);
        else
            extra = true;
    }
    if (extra)
        out.push('other');
    return canonical(out, TRADITIONS);
}
/**
 * Voorstel per traditie. Bewust kort: ontbreekt een traditie, dan stellen we niets voor.
 * Toevoegen = review (zie kop van dit bestand).
 */
export const TRADITION_SUGGESTIONS = {
    turkish: { faith: 'islam', foodRules: ['halal'], daySets: ['tr_national', 'islamic'] },
    moroccan: { faith: 'islam', foodRules: ['halal'], daySets: ['islamic'] },
};
/**
 * Keukens waarbij de voorstelkaart kan verschijnen. Alleen om `suggestion_answered` op te ruimen
 * (normalizeHouseholdProfile): staat geen van deze keukens meer aan, dan vergeten we dat de kaart
 * ooit beantwoord is (minimalisatie; er blijft geen spoor van een eerdere keuze achter).
 */
export function kitchensWithSuggestion() {
    const out = [];
    for (const t of TRADITIONS)
        if (TRADITION_SUGGESTIONS[t])
            out.push(...TRADITION_KITCHENS[t]);
    return canonical(out, KITCHENS);
}
/**
 * Het voorstel dat de kaart "Past dit bij jullie?" nu moet tonen, of null. Regels:
 * - is de kaart ooit beantwoord ("Ja, overnemen" of "Zelf kiezen"), dan nooit meer (ook niet als
 *   er later Marokkaans bij komt: dat voelt opdringerig);
 * - geloof alleen voorstellen als geloof nog NIET beantwoord is (null); koos het gezin al een
 *   geloof of "Geen geloof", dan stellen we geen ander geloof voor;
 * - eetregels van een voorstel alleen als geloof open is of het voorgestelde geloof bevat (na
 *   "Geen geloof" geen halal voorstellen);
 * - wat al gekozen is, stellen we niet nog eens voor.
 */
export function pendingSuggestion(state) {
    if (state.answered)
        return null;
    const faiths = [];
    const rules = [];
    const sets = [];
    for (const t of canonical(state.traditions, TRADITIONS)) {
        const s = TRADITION_SUGGESTIONS[t];
        if (!s)
            continue;
        const open = state.faiths === null;
        const hasFaith = state.faiths !== null && state.faiths.indexOf(s.faith) >= 0;
        if (!open && !hasFaith)
            continue;
        if (open)
            faiths.push(s.faith);
        rules.push(...s.foodRules);
        sets.push(...s.daySets);
    }
    const out = {
        faiths: canonical(faiths, FAITHS),
        foodRules: canonical(rules, FOOD_RULES).filter((r) => state.foodRules.indexOf(r) < 0),
        daySets: canonical(sets, DAY_SETS),
    };
    if (out.faiths.length === 0 && out.foodRules.length === 0)
        return null;
    return out;
}
/**
 * "Ja, overnemen": voegt geloof en eetregels TOE (niets wordt weggehaald) en onthoudt dat de kaart
 * beantwoord is. Zet NOOIT bijzondere dagen aan: die staan niet in het resultaat.
 */
export function acceptSuggestion(state, suggestion) {
    return {
        faiths: canonical([...(state.faiths ?? []), ...suggestion.faiths], FAITHS),
        foodRules: canonical([...state.foodRules, ...suggestion.foodRules], FOOD_RULES),
        answered: true,
    };
}
/** "Zelf kiezen": niets overnemen, alleen onthouden dat de kaart beantwoord is. */
export function declineSuggestion() {
    return { answered: true };
}
/** Volgorde van de geloofskeuzes: wat bij de gekozen tradities past eerst, verder vast. */
export function orderFaithOptions(traditions) {
    const first = [];
    for (const t of canonical(traditions, TRADITIONS)) {
        const s = TRADITION_SUGGESTIONS[t];
        if (s)
            first.push(s.faith);
    }
    const lead = canonical(first, FAITHS);
    return [...lead, ...FAITHS.filter((f) => lead.indexOf(f) < 0)];
}
/** Eetregels die vaak bij een geloof horen (alleen voor de VOLGORDE van de keuzes). */
const FAITH_FOOD_ORDER = {
    islam: ['halal', 'no_pork', 'no_alcohol'],
    christianity: [],
    judaism: ['kosher', 'no_pork'],
    hinduism: ['no_beef', 'vegetarian'],
    other: [],
};
/**
 * Volgorde van de eetregels: wat bij de gekozen tradities en geloven past eerst, verder vast.
 * Alleen volgorde; niets wordt aangevinkt.
 */
export function orderFoodRuleOptions(traditions, faiths = null) {
    const first = [];
    for (const f of canonical(faiths ?? [], FAITHS))
        first.push(...FAITH_FOOD_ORDER[f]);
    for (const t of canonical(traditions, TRADITIONS)) {
        const s = TRADITION_SUGGESTIONS[t];
        if (s)
            first.push(...s.foodRules);
    }
    const seen = [];
    for (const r of first)
        if (seen.indexOf(r) < 0)
            seen.push(r);
    return [...seen, ...FOOD_RULES.filter((r) => seen.indexOf(r) < 0)];
}
//# sourceMappingURL=traditions.js.map